/**
 * «Примерить зону» — the request the battle HUD / the sheet hands to the canvas: fit this spell's area from this token.
 * A tiny external store (the HUD and the canvas are far apart in the tree). Local to the player's browser — a fit that is
 * not kept sends nothing to anyone.
 */
import { useSyncExternalStore } from 'react';
import type { SpellAoe } from '@roll1/shared/spell-aoe';

export interface SpellAoeFitRequest {
  spellName: string;
  aoe: SpellAoe;
  /** 0 = Self, null = no measurable limit (see parseSpellRangeFt). */
  rangeFt: number | null;
  /** The caster token on the current map. */
  tokenId: string;
  /** Changes with every request, so asking again restarts the fit. */
  nonce: number;
}

let current: SpellAoeFitRequest | null = null;
let seq = 0;
const listeners = new Set<() => void>();
const emit = (): void => { for (const l of listeners) l(); };

export function requestSpellAoeFit(req: Omit<SpellAoeFitRequest, 'nonce'>): void {
  current = { ...req, nonce: ++seq };
  emit();
}

export function cancelSpellAoeFit(): void {
  if (current === null) return;
  current = null;
  emit();
}

// Every canvas on screen (the Studio shows two) reads the same request; each says which tokens it has, so a canvas without
// the caster does not end a fit that another canvas is showing.
const canvases = new Set<() => ReadonlySet<string>>();

export function registerFitCanvas(getTokenIds: () => ReadonlySet<string>): () => void {
  canvases.add(getTokenIds);
  return () => { canvases.delete(getTokenIds); };
}

export function tokenOnAnyCanvas(tokenId: string): boolean {
  for (const get of canvases) if (get().has(tokenId)) return true;
  return false;
}

export function getSpellAoeFitRequest(): SpellAoeFitRequest | null {
  return current;
}

export function useSpellAoeFitRequest(): SpellAoeFitRequest | null {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    getSpellAoeFitRequest,
    () => null,
  );
}
